import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import {
    parameterizablePasswordOrAKVtoSecretBase,
    parameterizableSecretBaseToModel
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { Constants } from 'app/shared/shared-constants';
import { GoogleBigQueryV2LinkedService } from 'app/model/linkedservice/google-bigquery-v2.linkedservice';
import { googleBigQueryAuthTypes } from 'app/model/linkedservice/google-bigquery.linkedservice';
import { ValueAdapter } from '../value.adapter';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(GoogleBigQueryV2LinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: GlobalVariables.enableGBQVersion ? '*' : undefined,
            typeProperties: {
                projectId: '*',
                authenticationType: '*',
                clientId: '*',
                clientSecret: 'secret',
                refreshToken: 'secret',
                encryptedCredential: '*',
                keyFileContent: 'secret'
            },
            parameters: '*'
        }
    }
})
export class GoogleBigQueryV2LinkedServiceAdapter extends LinkedServiceAdapter<GoogleBigQueryV2LinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => GoogleBigQueryV2LinkedService {
        return GoogleBigQueryV2LinkedService;
    }

    protected model(): IModel {
        return GoogleBigQueryV2LinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(
        linkedService: GoogleBigQueryV2LinkedService,
        linkedServiceResource: Model.LinkedServiceResource
    ): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.GoogleBigQueryV2LinkedService;
        properties.type = Metamodel.modelFor(GoogleBigQueryV2LinkedService).name;
        if (this._hasVersion) {
            properties.version = linkedService.version;
        }
        let clientSecret: string | Model.SecretBase;
        let refreshToken: string | Model.SecretBase;
        let keyFileContent: string | Model.SecretBase;
        let clientId: string;
        if (linkedService.authenticationType === googleBigQueryAuthTypes.UserAuthentication) {
            clientId = this.valueToResource(linkedService, GoogleBigQueryV2LinkedService.CLIENT_ID);
            clientSecret = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                GoogleBigQueryV2LinkedService.CLIENT_SECRET,
                this.context
            );
            refreshToken = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                GoogleBigQueryV2LinkedService.REFRESH_TOKEN,
                this.context
            );
        } else if (linkedService.authenticationType === googleBigQueryAuthTypes.ServiceAuthentication) {
            keyFileContent = parameterizablePasswordOrAKVtoSecretBase(
                linkedService,
                GoogleBigQueryV2LinkedService.KEY_FILE_CONTENT,
                this.context
            );
        }
        properties.typeProperties = {
            projectId: this.valueToResource(linkedService, GoogleBigQueryV2LinkedService.PROJECT_ID),
            authenticationType: linkedService.authenticationType,
            clientId,
            clientSecret,
            refreshToken,
            keyFileContent
        };
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(
        linkedServiceResource: Model.LinkedServiceResource,
        linkedService: GoogleBigQueryV2LinkedService
    ): Promise<GoogleBigQueryV2LinkedService> {
        const properties = linkedServiceResource.properties as Model.GoogleBigQueryV2LinkedService;
        const typeProperties: Model.GoogleBigQueryV2LinkedServiceTypeProperties = properties.typeProperties;

        if (this._hasVersion || properties.version) {
            linkedService.version = properties.version;
        }

        ValueAdapter.toModel(linkedService, GoogleBigQueryV2LinkedService.PROJECT_ID, typeProperties.projectId);
        linkedService.authenticationType = typeProperties.authenticationType;
        ValueAdapter.toModel(
            linkedService,
            GoogleBigQueryV2LinkedService.CLIENT_ID,
            typeProperties.clientId || Constants.maskedCredential
        );
        parameterizableSecretBaseToModel(
            linkedService,
            GoogleBigQueryV2LinkedService.CLIENT_SECRET,
            typeProperties.clientSecret,
            this.context,
            typeProperties.authenticationType === googleBigQueryAuthTypes.UserAuthentication
        );
        parameterizableSecretBaseToModel(
            linkedService,
            GoogleBigQueryV2LinkedService.REFRESH_TOKEN,
            typeProperties.refreshToken,
            this.context,
            typeProperties.authenticationType === googleBigQueryAuthTypes.UserAuthentication
        );
        parameterizableSecretBaseToModel(
            linkedService,
            GoogleBigQueryV2LinkedService.KEY_FILE_CONTENT,
            typeProperties.keyFileContent,
            this.context,
            typeProperties.authenticationType === googleBigQueryAuthTypes.ServiceAuthentication
        );

        return linkedService;
    }

    private get _hasVersion(): boolean {
        return GlobalVariables.enableGBQVersion;
    }
}
